/**
 * Dashboard — the page SHELL.
 *
 * One fetch, three looks. This shell owns the single `useQuery(['dashboard'])`
 * call, the loading/error states and <OnboardingNextCard/>, then renders the
 * presentational variant(s). The variants are pure functions of `data` — no
 * duplicated fetching — so the look can change without touching the data path.
 *
 * TWO THEMED LOOKS (default): the Warm (Direction A) and Depth (Direction C)
 * layouts are BOTH rendered; scoped CSS in dashboard/dashboard-theme.css reveals
 * Warm in light mode and Depth in dark mode, keyed on the same three-state
 * token pattern as index.css. No JS reads the theme, so there is no flash.
 *
 * ROLLBACK TO CONSOLE: change the one line below to
 *     const DASHBOARD_MODE: DashboardMode = 'console';
 * and the preserved original "Console" dashboard renders instead, unchanged.
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { cn } from '../lib/cn';
import { useApiError } from '../lib/useApiError';
import { ErrorNotice, Skeleton } from '../components/ui';
import { OnboardingNextCard } from '../components/Onboarding';
import { BoardReportButton } from '../components/BoardReportButton';
import type { DashboardData } from './dashboard/types';
import { DashboardConsole } from './dashboard/DashboardConsole';
import { DashboardWarm } from './dashboard/DashboardWarm';
import { DashboardDepth } from './dashboard/DashboardDepth';
// Self-hosted fonts for the themed variants (bundled by Vite, no Google CDN).
import './dashboard/fonts';
import './dashboard/dashboard-theme.css';

type DashboardMode = 'theme' | 'console';

/**
 * 'theme'   -> Direction A in light, Direction C in dark (default).
 * 'console' -> the preserved original Console dashboard (rollback).
 * This is the ONE line to change to revert to Console.
 */
const DASHBOARD_MODE: DashboardMode = 'theme';

export function DashboardPage() {
  const { t } = useTranslation();
  const describeError = useApiError();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<DashboardData>('/dashboard'),
  });

  if (isLoading) return <DashboardSkeleton />;

  if (error) {
    return (
      <div className="oc-page">
        <ErrorNotice message={describeError(error)} onRetry={() => void refetch()} retryLabel={t('common.retry')} />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="oc-page space-y-4 animate-fade-in">
      {/* The single next action, above the numbers — rendered in every look. */}
      <OnboardingNextCard />

      {/* One-click executive PDF; renders nothing without report.read. */}
      <div className="flex justify-end">
        <BoardReportButton />
      </div>

      {DASHBOARD_MODE === 'console' ? (
        <DashboardConsole data={data} />
      ) : (
        <>
          {/* Both are rendered; CSS reveals exactly one per theme. */}
          <DashboardWarm data={data} />
          <DashboardDepth data={data} />
        </>
      )}
    </div>
  );
}

/** Skeleton shaped like the real page, so the layout does not jump on load. */
function DashboardSkeleton() {
  const PANEL = 'rounded-xl border border-line bg-surface-raised';
  return (
    <div className="oc-page space-y-4">
      <div className="flex items-center gap-2.5">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-5 w-10" />
      </div>
      <div className="oc-chart-container">
        <div
          className={cn(
            PANEL, 'overflow-hidden grid grid-cols-2 gap-px bg-line',
            '[@container(min-width:560px)]:grid-cols-5',
          )}
        >
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className={cn(
                'bg-surface-raised px-3.5 py-3',
                i === 0 && 'col-span-2 [@container(min-width:560px)]:col-span-1',
              )}
            >
              <Skeleton className="h-3 w-20" />
              <Skeleton className="mt-2 h-6 w-14" />
              <Skeleton className="mt-2 h-3 w-24" />
            </div>
          ))}
        </div>
      </div>
      <Skeleton className="h-64 rounded-xl" />
      <div className="oc-chart-container">
        <div className="grid gap-4 [@container(min-width:640px)]:grid-cols-2">
          <Skeleton className="h-44 rounded-xl" />
          <Skeleton className="h-56 rounded-xl" />
        </div>
      </div>
    </div>
  );
}
